import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  UserMinusIcon,
  StarIcon,
  UserIcon as UserIconOutline,
} from '@heroicons/react/24/outline';
import { teamApi, Team, TeamMember } from '@/services/teamApi';
import { roleApi } from '@/services/roleApi';
import api from '@/services/api';
import { PermissionGuard } from '@/components/Auth/PermissionGuard';

// `member.userId` is populated into the user object by the backend
// (members.userId path) whenever a team is fetched — these normalize
// access to it since it's a string only in the rare unpopulated case.
const getMemberUserId = (member: TeamMember): string =>
  typeof member.userId === 'string' ? member.userId : member.userId._id;
const getMemberUser = (member: TeamMember) =>
  typeof member.userId === 'string' ? undefined : member.userId;
import { usePermissions } from '@/hooks/usePermissions';
import Pagination from '@/components/Common/Pagination';

const TeamsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams', currentPage, search, showActiveOnly],
    queryFn: () => teamApi.getAll({
      page: currentPage,
      limit,
      search: search || undefined,
      isActive: showActiveOnly ? true : undefined,
      includeMembers: true,
    }).then(res => res.data.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => teamApi.delete(id),
    onSuccess: () => {
      toast.success('Team deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to delete team'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (team: Team) => teamApi.update(team._id, { isActive: !team.isActive }),
    onSuccess: () => {
      toast.success('Team status updated');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update team'),
  });

  const handleEdit = (team: Team) => {
    setSelectedTeam(team);
    setShowEditModal(true);
  };

  const handleView = (team: Team) => {
    setSelectedTeam(team);
    setShowViewModal(true);
  };

  const handleManageMembers = (team: Team) => {
    setSelectedTeam(team);
    setShowManageMembersModal(true);
  };

  const handleDelete = (team: Team) => {
    if (window.confirm(`Are you sure you want to delete the "${team.name}" team? This action cannot be undone.`)) {
      deleteMutation.mutate(team._id);
    }
  };

  const handleToggleStatus = (team: Team) => {
    toggleStatusMutation.mutate(team);
  };

  const getStatusBadge = (team: Team) => {
    if (!team.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const getMemberRoleIcon = (role: string) => {
    return role === 'lead' ? (
      <StarIcon className="w-4 h-4 text-yellow-500" title="Team Lead" />
    ) : (
      <UserIconOutline className="w-4 h-4 text-gray-400" title="Member" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Teams</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage teams and their members</p>
        </div>
        <PermissionGuard module="teams" action="create">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Team
          </button>
        </PermissionGuard>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search teams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="activeOnly"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="activeOnly" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Active only
            </label>
          </div>
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading teams...</p>
          </div>
        ) : !teamsData?.teams?.length ? (
          <div className="p-8 text-center text-gray-500">
            <UserGroupIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
            <p>No teams found</p>
            <PermissionGuard module="teams" action="create">
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-blue-600 hover:text-blue-800"
              >
                Create your first team
              </button>
            </PermissionGuard>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {teamsData.teams.map((team: Team) => (
                  <tr key={team._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {team.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {team.description || 'No description'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            by {team.createdBy?.name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {team.members.length} members
                        </span>
                        {team.members.length > 0 && (
                          <div className="flex -space-x-1">
                            {team.members.slice(0, 3).map((member) => (
                              <div
                                key={getMemberUserId(member)}
                                className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center"
                                title={getMemberUser(member)?.name || 'Unknown'}
                              >
                                {getMemberUser(member)?.name?.charAt(0) || '?'}
                              </div>
                            ))}
                            {team.members.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-gray-400 border-2 border-white flex items-center justify-center text-xs text-white">
                                +{team.members.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(team)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(team.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleView(team)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View team"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleManageMembers(team)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Manage members"
                        >
                          <UserGroupIcon className="h-4 w-4" />
                        </button>
                        <PermissionGuard module="teams" action="update">
                          <button
                            onClick={() => handleEdit(team)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Edit team"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(team)}
                            className={team.isActive ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"}
                            title={team.isActive ? "Deactivate" : "Activate"}
                          >
                            {team.isActive ? <XCircleIcon className="h-4 w-4" /> : <CheckCircleIcon className="h-4 w-4" />}
                          </button>
                        </PermissionGuard>
                        <PermissionGuard module="teams" action="delete">
                          <button
                            onClick={() => handleDelete(team)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete team"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {teamsData?.pagination && teamsData.pagination.pages > 1 && (
          <div className="px-6 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={teamsData.pagination.pages}
              onPageChange={setCurrentPage}
              totalItems={teamsData.pagination.total}
              itemsPerPage={limit}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTeamModal
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showEditModal && selectedTeam && (
        <EditTeamModal
          team={selectedTeam}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showViewModal && selectedTeam && (
        <ViewTeamModal
          team={selectedTeam}
          onClose={() => setShowViewModal(false)}
        />
      )}

      {showManageMembersModal && selectedTeam && (
        <ManageMembersModal
          team={selectedTeam}
          onClose={() => setShowManageMembersModal(false)}
        />
      )}
    </div>
  );
};

// Create Team Modal Component
const CreateTeamModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: usersData } = useQuery({
    queryKey: ['users-simple'],
    queryFn: async () => {
      // Must go through the shared `api` instance, not a bare fetch() — the
      // interceptor there is what actually attaches the Authorization
      // header; a bare fetch() silently gets a 401 from this route's
      // authenticateToken middleware, and with no `response.ok` check the
      // code used to throw on `undefined.users`, which React Query
      // swallowed — the picker just stayed empty with no visible error.
      const response = await api.get<{ success: boolean; data: { users: Array<{ _id: string; name: string; email: string }> } }>(
        '/users',
        { params: { limit: 100 } }
      );
      return response.data.data.users;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; memberIds?: string[] }) =>
      teamApi.create(data),
    onSuccess: () => {
      toast.success('Team created successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onClose();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to create team'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({ 
      name: name.trim(), 
      description: description.trim(),
      memberIds: selectedMembers 
    });
  };

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Create Team</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Team Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Initial Members</label>
            <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
              {usersData?.map((user: any) => (
                <label key={user._id} className="flex items-center text-sm mb-2">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(user._id)}
                    onChange={() => toggleMember(user._id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  {user.name} ({user.email})
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || !name.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Creating...' : 'Create Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Team Modal Component
const EditTeamModal: React.FC<{
  team: Team;
  onClose: () => void;
}> = ({ team, onClose }) => {
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description || '');
  const [isActive, setIsActive] = useState(team.isActive);
  // Unset/empty means unrestricted — represented here as "every module checked".
  const [allowedModules, setAllowedModules] = useState<string[]>(team.allowedModules || []);
  const [restricted, setRestricted] = useState(!!team.allowedModules && team.allowedModules.length > 0);
  const queryClient = useQueryClient();

  const { data: permissionsMeta } = useQuery({
    queryKey: ['permissions-meta'],
    queryFn: () => roleApi.getPermissionsMeta().then(res => res.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string; isActive?: boolean }) =>
      teamApi.update(team._id, data),
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update team'),
  });

  const capabilitiesMutation = useMutation({
    mutationFn: (modules: string[]) => teamApi.setAllowedModules(team._id, modules),
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update allowed actions'),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await updateMutation.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        isActive
      });
      await capabilitiesMutation.mutateAsync(restricted ? allowedModules : []);
      toast.success('Team updated successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onClose();
    } catch {
      // Individual mutations already toast their own errors.
    }
  };

  const toggleModule = (name: string) => {
    setAllowedModules((prev) => (prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Team</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Team Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
          </div>
          <div>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={restricted}
                onChange={(e) => setRestricted(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Restrict allowed actions</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              When on, this team's members can only use the checked actions below — this only ever narrows
              what a member's own role already permits, never grants beyond it. Off means unrestricted.
            </p>
            {restricted && (
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto grid grid-cols-2 gap-1">
                {(permissionsMeta?.modules || []).map((module) => (
                  <label key={module.name} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={allowedModules.includes(module.name)}
                      onChange={() => toggleModule(module.name)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    />
                    {module.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading || capabilitiesMutation.isLoading || !name.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isLoading || capabilitiesMutation.isLoading ? 'Updating...' : 'Update Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Team Modal Component
const ViewTeamModal: React.FC<{
  team: Team;
  onClose: () => void;
}> = ({ team, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Team: {team.name}
        </h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">Team Details</h4>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Description:</strong> {team.description || 'No description'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                <strong>Status:</strong> {team.isActive ? 'Active' : 'Inactive'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                <strong>Created:</strong> {new Date(team.createdAt).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                <strong>Created by:</strong> {team.createdBy?.name || 'Unknown'}
              </p>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">Members ({team.members.length})</h4>
            <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
              {team.members.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No members in this team</p>
              ) : (
                <div className="space-y-2">
                  {team.members.map((member) => (
                    <div key={getMemberUserId(member)} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded">
                      <div className="flex items-center space-x-2">
                        {getMemberRoleIcon(member.role)}
                        <span className="text-sm text-gray-900 dark:text-white">
                          {getMemberUser(member)?.name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({getMemberUser(member)?.email})
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Joined {new Date(member.joinedAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Manage Members Modal Component
const ManageMembersModal: React.FC<{
  team: Team;
  onClose: () => void;
}> = ({ team, onClose }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const queryClient = useQueryClient();

  const { data: availableUsers } = useQuery({
    queryKey: ['available-users', team._id],
    queryFn: () => teamApi.getAvailableUsers(team._id).then(res => res.data.data),
    enabled: !!team._id,
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { userId: string; role: 'lead' | 'member' }) =>
      teamApi.addMember(team._id, data.userId, data.role),
    onSuccess: () => {
      toast.success('Member added successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['available-users', team._id] });
      setSelectedUser('');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to add member'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => teamApi.removeMember(team._id, userId),
    onSuccess: () => {
      toast.success('Member removed successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['available-users', team._id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to remove member'),
  });

  const updateRoleMutation = useMutation({
    mutationFn: (data: { userId: string; role: 'lead' | 'member' }) =>
      teamApi.updateMemberRole(team._id, data.userId, data.role),
    onSuccess: () => {
      toast.success('Member role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update role'),
  });

  const handleAddMember = (role: 'lead' | 'member' = 'member') => {
    if (!selectedUser) return;
    addMemberMutation.mutate({ userId: selectedUser, role });
  };

  const handleRemoveMember = (userId: string) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      removeMemberMutation.mutate(userId);
    }
  };

  const handleToggleRole = (userId: string, currentRole: 'lead' | 'member') => {
    const newRole = currentRole === 'lead' ? 'member' : 'lead';
    updateRoleMutation.mutate({ userId, role: newRole });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Manage Members: {team.name}
        </h3>
        
        {/* Add Members Section */}
        <div className="mb-6">
          <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">Add Members</h4>
          <div className="flex space-x-2">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a user to add...</option>
              {availableUsers?.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <PermissionGuard module="teams" action="update">
              <button
                onClick={() => handleAddMember('member')}
                disabled={!selectedUser || addMemberMutation.isLoading}
                className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <UserPlusIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleAddMember('lead')}
                disabled={!selectedUser || addMemberMutation.isLoading}
                className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                title="Add as Team Lead"
              >
                <StarIcon className="h-4 w-4" />
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Current Members Section */}
        <div>
          <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">
            Current Members ({team.members.length})
          </h4>
          <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
            {team.members.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No members in this team</p>
            ) : (
              <div className="space-y-2">
                {team.members.map((member) => (
                  <div key={getMemberUserId(member)} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded">
                    <div className="flex items-center space-x-2">
                      {getMemberRoleIcon(member.role)}
                      <span className="text-sm text-gray-900 dark:text-white">
                        {getMemberUser(member)?.name || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({getMemberUser(member)?.email})
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PermissionGuard module="teams" action="update">
                        <button
                          onClick={() => handleToggleRole(getMemberUserId(member), member.role)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                          title={member.role === 'lead' ? 'Make member' : 'Make lead'}
                        >
                          {member.role === 'lead' ? 'Make Member' : 'Make Lead'}
                        </button>
                        <button
                          onClick={() => handleRemoveMember(getMemberUserId(member))}
                          className="text-red-600 hover:text-red-900"
                          title="Remove member"
                        >
                          <UserMinusIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function for member role icon
const getMemberRoleIcon = (role: string) => {
  return role === 'lead' ? (
    <StarIcon className="w-4 h-4 text-yellow-500" title="Team Lead" />
  ) : (
    <UserIconOutline className="w-4 h-4 text-gray-400" title="Member" />
  );
};

export default TeamsPage;
